import { useCallback, useEffect, useMemo, useState } from "react";

import { type AegAuditLog, type AegOccurrenceId, fetchAegLogs } from "../api/aegOccurrences";
import type { StageKey } from "../components/aeg/types";
import { buildAegHistoryEntries } from "../utils/aegSupportHistory";

export type AegHistoryFilters = {
  stage: StageKey | "";
  user: string;
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
};

const EMPTY_FILTERS: AegHistoryFilters = { stage: "", user: "", from: "", to: "" };

export const useAegSupportHistory = (aegOccurrenceId: AegOccurrenceId) => {
  const [logs, setLogs] = useState<AegAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<AegHistoryFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<AegHistoryFilters>(EMPTY_FILTERS);

  const loadLogs = useCallback(async () => {
    if (!aegOccurrenceId) {
      setLogs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setLogs(await fetchAegLogs(aegOccurrenceId));
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [aegOccurrenceId]);

  useEffect(() => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    void loadLogs();
  }, [loadLogs]);

  const allEntries = useMemo(() => buildAegHistoryEntries(logs), [logs]);

  const entries = useMemo(() => {
    const user = applied.user.trim().toLowerCase();
    const from = applied.from ? new Date(`${applied.from}T00:00:00`).getTime() : undefined;
    const to = applied.to ? new Date(`${applied.to}T23:59:59.999`).getTime() : undefined;

    return allEntries.filter((entry) => {
      const at = new Date(entry.createdAt).getTime();
      if (applied.stage && entry.stage !== applied.stage) return false;
      if (user && !entry.user.toLowerCase().includes(user)) return false;
      if (from !== undefined && at < from) return false;
      if (to !== undefined && at > to) return false;
      return true;
    });
  }, [allEntries, applied]);

  const updateDraft = (patch: Partial<AegHistoryFilters>) => setDraft((prev) => ({ ...prev, ...patch }));
  const applyFilters = () => setApplied(draft);

  return {
    entries,
    total: allEntries.length,
    loading,
    filters: draft,
    updateFilters: updateDraft,
    applyFilters,
    reload: loadLogs,
  };
};

export type AegSupportHistoryController = ReturnType<typeof useAegSupportHistory>;
